// 연결 상태 추적을 위한 변수들
let connectionStartTime = null;
let connectionState = 'disconnected';
// 단순화된 구독 구조: 각 구독을 독립적으로 관리
const subscriptions = new Map(); // key: uniqueSubscriberUID, value: { symbol, resolution, lastBar, callback }
// TradingView 원본 subscriberUID -> 내부 고유 subscriberUID 매핑
const subscriberUIDMapping = new Map();
/**
 * Event Bus를 통한 실시간 데이터 처리 설정
 * 웹소켓 데이터를 TradingView 차트에 피딩
 */
function setupEventBusIntegration() {
    // Event Bus를 통한 시장 데이터 업데이트 감지
    if (typeof window !== 'undefined') {
        // 동적 import로 Event Bus 사용
        import('@template/utils').then(({ globalEventBus, MARKET_EVENTS }) => {
            globalEventBus.on(MARKET_EVENTS.DATA_UPDATED, (data) => {
                const price = data.sellPrice ?? 0;
                // 유효하지 않은 가격은 차트에 넣지 않음 (scale 오염 방지)
                if (!Number.isFinite(price) || price < 0) {
                    console.warn('[TradingView Streaming] Invalid price, skip bar:', {
                        symbol: data.symbol,
                        price,
                    });
                    return;
                }
                // TradingView 차트용 Bar 데이터 생성
                const realtimeBar = {
                    time: data.timestamp || Date.now(),
                    open: price,
                    high: price,
                    low: price,
                    close: price,
                    volume: data.volume || 0,
                };
                // TradingView 차트 업데이트
                updateBarsForSymbol(data.symbol, realtimeBar);
                // console.log(`[TradingView Streaming] ${data.symbol} 차트 업데이트:`, {
                //   time: new Date(realtimeBar.time).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
                //   price: realtimeBar.close,
                //   volume: realtimeBar.volume,
                // });
            });
        });
    }
}
/**
 * WebSocket 연결 초기화
 * Event Bus 통합만 설정
 */
function initializeSocket() {
    // Event Bus 통합 설정
    setupEventBusIntegration();
}
/**
 * 심볼별 모든 구독 Bar 업데이트
 * @param symbol - 심볼명
 * @param realtimeBar - 실시간 Bar 데이터
 */
export function updateBarsForSymbol(symbol, realtimeBar) {
    let matchedCount = 0;
    // 해당 심볼의 모든 구독 찾기
    subscriptions.forEach((subscription, subscriberUID) => {
        if (subscription.symbol === symbol) {
            matchedCount++;
            const updatedBar = createOrUpdateBar(realtimeBar, subscription.lastBar, subscription.resolution);
            // 구독의 lastBar 업데이트
            subscription.lastBar = updatedBar;
            // 콜백 호출
            try {
                subscription.callback(updatedBar);
            }
            catch (error) {
                console.error(`[TradingView Streaming] 구독 ${subscriberUID} 콜백 오류:`, error);
            }
        }
    });
    // console.log(
    //   `[updateBarsForSymbol] 완료: ${symbol} - ${matchedCount}개 구독`
    // );
}
/**
 * Resolution별 Bar 생성/업데이트 (연속성 보장)
 * @param realtimeBar - 실시간 Bar 데이터
 * @param lastBar - 마지막 Bar 데이터
 * @param resolution - 시간 해상도
 * @returns 업데이트된 Bar 데이터
 */
function createOrUpdateBar(realtimeBar, lastBar, resolution) {
    // 정확한 Bar 시작 시간 계산
    const currentBarStart = getBarStartTime(realtimeBar.time, resolution);
    if (!lastBar) {
        // 첫 번째 Bar 생성: 히스토리 데이터가 없는 경우
        const newBar = {
            time: currentBarStart * 1000,
            open: realtimeBar.close, // 첫 번째 Bar는 실시간 가격을 시가로 사용
            high: realtimeBar.close,
            low: realtimeBar.close,
            close: realtimeBar.close,
            volume: realtimeBar.volume,
        };
        return newBar;
    }
    // 이전 Bar의 시작 시간 계산
    const lastBarStart = getBarStartTime(lastBar.time, resolution);
    if (currentBarStart > lastBarStart) {
        // 새로운 Bar 생성: 이전 Bar와 정확히 연결
        const openPrice = lastBar.close; // 🎯 핵심: 이전 Bar의 종가를 시가로 사용
        const closePrice = realtimeBar.close;
        const newBar = {
            time: currentBarStart * 1000,
            open: openPrice, // 🎯 이전 Bar의 종가
            high: Math.max(openPrice, closePrice), // 🎯 open과 close 중 높은 값
            low: Math.min(openPrice, closePrice), // 🎯 open과 close 중 낮은 값
            close: closePrice, // 🎯 실시간 가격
            volume: realtimeBar.volume || 0,
        };
        // console.log(`[${resolution}] 새로운 Bar 생성:`, {
        //   이전Bar: new Date(lastBarStart * 1000).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
        //   새Bar: new Date(currentBarStart * 1000).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
        //   시간차: `${(currentBarStart - lastBarStart) / 60}분`,
        //   이전종가: openPrice,
        //   새시가: openPrice,
        //   새종가: closePrice,
        //   갭: openPrice === closePrice ? '없음' : `${Math.abs(openPrice - closePrice).toFixed(2)}`,
        // });
        return newBar;
    }
    else if (currentBarStart === lastBarStart) {
        // 같은 Bar 내에서 업데이트: OHLC만 업데이트, 시간은 그대로
        const updatedBar = {
            ...lastBar,
            high: Math.max(lastBar.high, realtimeBar.high || realtimeBar.close),
            low: Math.min(lastBar.low, realtimeBar.low || realtimeBar.close),
            close: realtimeBar.close,
            volume: lastBar.volume + (realtimeBar.volume || 0),
        };
        return updatedBar;
    }
    else {
        // 시간이 뒤처진 경우 (네트워크 지연 등): 기존 Bar 유지
        console.warn(`[${resolution}] 시간 역전 감지:`, {
            현재시간: new Date(realtimeBar.time).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
            Bar시간: new Date(lastBar.time).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
            차이: `${(lastBar.time - realtimeBar.time) / 1000}초`,
        });
        return lastBar;
    }
}
/**
 * 정확한 Bar 시작 시간 계산
 * @param timestamp - Unix timestamp (밀리초)
 * @param resolution - 시간 해상도 문자열
 * @returns Bar 시작 시간 (초 단위)
 */
function getBarStartTime(timestamp, resolution) {
    const timeInSeconds = Math.floor(timestamp / 1000);
    // 1M(1달): 달력상 해당 월 시작(UTC) 사용. 고정 30일 구간은 달력 월과 어긋나 API 이번 달 봉과 실시간이 다른 구간으로 나뉘는 문제 방지.
    if (resolution === '1M') {
        const d = new Date(timestamp);
        const startOfMonthMs = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0);
        return Math.floor(startOfMonthMs / 1000);
    }
    const interval = getTimeInterval(resolution);
    return Math.floor(timeInSeconds / interval) * interval;
}
/**
 * 시간 간격 계산 (초 단위)
 * Bar 구간 간격. 1M은 달력 월이 28~31일이라 대표값(30일) 반환. 실제 1M Bar 시작은 getBarStartTime에서 달력 월 기준으로 계산.
 * @param resolution - 시간 해상도 문자열
 * @returns 초 단위 시간 간격
 */
function getTimeInterval(resolution) {
    switch (resolution) {
        case '1':
            return 60;
        case '5':
            return 5 * 60;
        case '15':
            return 15 * 60;
        case '30':
            return 30 * 60;
        case '60':
            return 60 * 60;
        case '240':
            return 4 * 60 * 60;
        case '1D':
            return 24 * 60 * 60;
        case '1W':
            return 7 * 24 * 60 * 60;
        case '1M':
            return 30 * 24 * 60 * 60; // 대표값. 연속성 검증은 validateBarContinuity에서 getBarStartTime 사용
        default:
            return 24 * 60 * 60; // 기본값: 1일
    }
}
/**
 * Bar 연속성 검증 (디버깅용)
 * @param bars - Bar 배열
 * @param resolution - 시간 해상도
 * @returns 연속성 검증 결과
 */
function validateBarContinuity(bars, resolution) {
    if (bars.length < 2)
        return true;
    const toleranceMs = resolution === '1M' ? 24 * 60 * 60 * 1000 : 1000; // 1M: 1일 허용(달력 월 28~31일), 그외 1초
    for (let i = 1; i < bars.length; i++) {
        const prevBar = bars[i - 1];
        const currentBar = bars[i];
        const expectedTime = resolution === '1M'
            ? getBarStartTime(prevBar.time + 32 * 24 * 60 * 60 * 1000, '1M') * 1000 // 다음 달 시작(달력 기준)
            : prevBar.time + getTimeInterval(resolution) * 1000;
        const timeDiff = Math.abs(currentBar.time - expectedTime);
        if (timeDiff > toleranceMs) {
            console.error(`[${resolution}] Bar 연속성 오류:`, {
                prevBar: new Date(prevBar.time).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
                currentBar: new Date(currentBar.time).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
                expected: new Date(expectedTime).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
                차이: `${timeDiff / 1000}초`,
            });
            return false;
        }
    }
    console.log(`[${resolution}] Bar 연속성 검증 통과: ${bars.length}개 Bar`);
    return true;
}
// 소켓 초기화 (모듈 로드 시)
initializeSocket();
/**
 * 현재 구독 상태 확인 (디버깅용)
 * @returns 구독 상태 정보
 */
export function getSubscriptionStatus() {
    const symbolCounts = new Map();
    subscriptions.forEach((subscription) => {
        const count = symbolCounts.get(subscription.symbol) || 0;
        symbolCounts.set(subscription.symbol, count + 1);
    });
    return {
        connected: true, // WebSocket 모드에서는 항상 연결된 것으로 간주
        connectionState: connectionState,
        connectionDuration: connectionStartTime ? Date.now() - connectionStartTime : 0,
        reconnectAttempts: 0,
        subscriptions: Array.from(subscriptions.entries()).map(([uid, sub]) => ({
            uid,
            symbol: sub.symbol,
            resolution: sub.resolution,
            lastBar: sub.lastBar,
        })),
        symbolCounts: Object.fromEntries(symbolCounts),
        totalSubscriptions: subscriptions.size,
    };
}
/**
 * 실시간 데이터 구독 함수
 * @param symbolInfo - 심볼 정보
 * @param resolution - 시간 해상도
 * @param onRealtimeCallback - 실시간 데이터 콜백
 * @param subscriberUID - 구독자 고유 ID
 * @param onResetCacheNeededCallback - 캐시 리셋 콜백 (사용 안함)
 * @param lastDailyBar - 마지막 일봉 데이터
 */
export function subscribeOnStream(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback, lastDailyBar) {
    const symbol = symbolInfo.full_name || symbolInfo.name || '';
    if (!symbol) {
        console.error('[subscribeOnStream] 심볼 정보 없음:', symbolInfo);
        return;
    }
    // 같은 심볼에서 TradingView subscriberUID가 재사용될 수 있어 내부 저장 키는 고유 ID로 관리
    const uniqueId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const uniqueSubscriberUID = `${subscriberUID}_${uniqueId}`;
    // lastDailyBar 없으면 bar를 만들지 않도록 처리
    const subscription = {
        symbol,
        resolution,
        lastBar: lastDailyBar ?? null,
        callback: onRealtimeCallback,
    };
    subscriberUIDMapping.set(subscriberUID, uniqueSubscriberUID);
    subscriptions.set(uniqueSubscriberUID, subscription);
    // WebSocket 모드에서는 실제 구독은 WebSocketService에서 처리됨
}
/**
 * 구독 해제 함수
 * @param subscriberUID - 구독자 고유 ID
 */
export function unsubscribeFromStream(subscriberUID) {
    const uniqueSubscriberUID = subscriberUIDMapping.get(subscriberUID);
    if (!uniqueSubscriberUID) {
        return;
    }
    const subscription = subscriptions.get(uniqueSubscriberUID);
    if (!subscription) {
        subscriberUIDMapping.delete(subscriberUID);
        return;
    }
    // 구독 제거
    subscriptions.delete(uniqueSubscriberUID);
    subscriberUIDMapping.delete(subscriberUID);
}
/**
 * WebSocket 연결 상태 확인
 * @returns 연결 상태 (항상 true)
 */
export function isConnected() {
    return true; // WebSocket 모드에서는 항상 연결된 것으로 간주
}
/**
 * 완전한 정리 함수 (페이지 언로드 시 호출)
 */
export function cleanup() {
    // 구독 상태 정리
    subscriptions.clear();
    subscriberUIDMapping.clear();
    // 상태 초기화
    connectionStartTime = null;
    connectionState = 'disconnected';
}
/**
 * 연결 상태 정보 가져오기
 * @returns 연결 상태 정보
 */
export function getConnectionState() {
    return {
        state: connectionState,
        connected: true,
        reconnectAttempts: 0,
        maxReconnectAttempts: 0,
        connectionDuration: connectionStartTime ? Date.now() - connectionStartTime : 0,
    };
}
//# sourceMappingURL=streaming.js.map